import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { postGetSettings } from '../api/auth';
import { postGetUgcSettings } from '../api/meta';
import {
  ArticleData,
  IAppSettings,
  IConversationNpcItem,
  IConversationNpcItemV2,
  IMediaConfig,
  INpcPoolItem,
  IRecentlyChatNpcData,
  ITaskNpcItem,
} from '../types';

interface IUserCenterPopData {
  open: boolean;
}

export const getSettings = createAsyncThunk('global/getSettings', async () => {
  return await postGetSettings();
});

export const getUgcSettings = createAsyncThunk('global/getUgcSettings', async () => {
  return await postGetUgcSettings();
});

export interface ISettings {
  appOnline: boolean;
  iosDownloadUrl: string;
  androidDownloadUrl: string;
  downloadQrcode: string;
  npcShareUrl: string;
  medias: string[];
  shareMedias: string[];
  mediaConfigs: IMediaConfig[];
  minimumAvailableAge: number;
  autoPlayAudio: boolean;
  chatShareMedias: string[]; // 聊天页面分享渠道
  conversationNpcList: IConversationNpcItem[];
  conversationNpcListV2: IConversationNpcItemV2[];
  npcPoolList: INpcPoolItem[];
  taskNpcList: ITaskNpcItem[];
  webSchemas?: ArticleData[];
  chatCloseDoorAdWhitelist?: number[];
  chatCloseDoorAdThreshold?: number;
  chatCloseDoorAdAppendThreshold?: number;
}

export interface ITrackCommonData {
  ip?: string; // node 层获取 header 的 ip 地址
  ip_region?: string; // 服务端接口返回的 en/ru 等
}

export interface ITrackSourceData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  edm_user_id?: string; // 召回邮件的登录 user id（用于埋点关联邮件登录用户和跳转到web未登录user id）
}
interface IDownloadPopData {
  open: boolean;
  qrcode: string;
}
interface ILoadingData {
  loading: boolean;
  msg?: string;
}

export interface IGlobalState {
  openContactModal: boolean;
  userCenterPopData: IUserCenterPopData;
  loadingData: ILoadingData;
  downloadPopData: IDownloadPopData;
  i18n: {
    locale: string;
  };
  sidebar: {
    currentTab: string;
    isCollapsed: boolean;
    chattedList: IRecentlyChatNpcData[];
    loading: boolean;
    currentChatNpcId: number;
  };
  trackCommonData: ITrackCommonData;
  trackSourceData: ITrackSourceData;
  playAudioFailedMid: null | string;
  openPluginSelectModal?: boolean;
  appSettings?: IAppSettings;
  xGroupSettings?: {
    url?: string;
    text?: string;
  };
}

export enum SidebarTab {
  MY = 'my',
  IMAGE_LIBRARY = 'image_library',
  FM = 'fm',
  AGENT = 'agent',
  AGENT_REPLICATE = 'agent_replicate',
}

const initialState: IGlobalState = {
  openContactModal: false,
  userCenterPopData: {
    open: false,
  },
  loadingData: {
    loading: false,
    msg: '',
  },
  downloadPopData: {
    open: false,
    qrcode: '',
  },
  i18n: {
    locale: 'en',
  },
  sidebar: {
    currentTab: '',
    isCollapsed: false,
    chattedList: [],
    loading: false,
    currentChatNpcId: 0, // 用于 chat 页标记当前聊天 npc 是否插入 chattedList
  },
  trackCommonData: {},
  trackSourceData: {},
  playAudioFailedMid: null,
};

export const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    setSidebarCurrentTab(state, action: PayloadAction<string>) {
      state.sidebar.currentTab = action.payload;
    },
    setSidebarIsCollapsed(state, action: PayloadAction<boolean>) {
      state.sidebar.isCollapsed = action.payload;
    },
    setI18nLocale(state, action: PayloadAction<string>) {
      state.i18n.locale = action.payload;
    },
    setTrackCommonData(state, action: PayloadAction<ITrackCommonData>) {
      state.trackCommonData = {
        ...state.trackCommonData,
        ...action.payload,
      };
    },
    setTrackSourceData(state, action: PayloadAction<ITrackSourceData>) {
      state.trackSourceData = {
        ...state.trackSourceData,
        ...action.payload,
      };
    },
    setSidebarCurrentChat(state, action: PayloadAction<{ data: IRecentlyChatNpcData }>) {
      const data = action.payload.data;
      if (!data.npc_id || state.sidebar.currentChatNpcId === data.npc_id) return;
      // 当前聊天的 npc，插入到 chattedList 最前面
      state.sidebar.currentChatNpcId = data.npc_id;
      const chattedList = state.sidebar.chattedList.filter((item) => item.npc_id !== data.npc_id);
      state.sidebar.chattedList = [
        { ...data, last_msg_time: new Date().getTime() },
        ...chattedList,
      ];
    },
    openDownloadPop(state, action: PayloadAction<Partial<IDownloadPopData>>) {
      state.downloadPopData.open = true;
      state.downloadPopData.qrcode = action.payload.qrcode || '';
    },
    setLoading(state, action: PayloadAction<boolean | ILoadingData>) {
      if (typeof action.payload === 'boolean') {
        state.loadingData = {
          loading: action.payload,
          msg: '',
        };
      } else {
        state.loadingData = {
          loading: action.payload.loading,
          msg: action.payload.msg || '',
        };
      }
    },
    setPlayAudioFailedMid(state, action: PayloadAction<string>) {
      if (state.playAudioFailedMid === null) state.playAudioFailedMid = action.payload;
    },
    closeDownloadPop(state) {
      state.downloadPopData.open = false;
    },
    openUserCenterPop(state) {
      state.userCenterPopData.open = true;
    },
    closeUserCenterPop(state) {
      state.userCenterPopData.open = false;
    },
    setOpenContactModal(state, action: PayloadAction<boolean>) {
      state.openContactModal = action.payload;
    },
    setPluginSelectModal(state, action: PayloadAction<boolean>) {
      state.openPluginSelectModal = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(getSettings.fulfilled, (state, action) => {
      state.appSettings = action.payload;
    });
    builder.addCase(getUgcSettings.fulfilled, (state, action) => {
      if (action.payload.data) {
        state.xGroupSettings = {
          url: action.payload.data.x_group_url,
          text: action.payload.data.x_group_text,
        };
      }
    });
  },
});

const { actions, reducer } = globalSlice;
export const {
  setSidebarCurrentTab,
  setSidebarIsCollapsed,
  setI18nLocale,
  setTrackCommonData,
  setTrackSourceData,
  setSidebarCurrentChat,
  openDownloadPop,
  setLoading,
  setPlayAudioFailedMid,
  closeDownloadPop,
  openUserCenterPop,
  closeUserCenterPop,
  setOpenContactModal,
  setPluginSelectModal,
} = actions;
export default reducer;
